import type { Column } from "@racoon/ui";
import { Namespace, type TFn } from "@racoon/i18n";
import { EMPTY_CELL } from "@racoon/lib";
import {
  containerGroupLabel,
  contactLabel,
  contractLabel,
  contactPhone,
  customerLabel,
  locationCode,
  locationNameLabel,
  locationPriorityLabel,
  locationTypeLabel,
  locationWorktime,
  pickupScheduleLabel,
  routeGroupsLabel,
  trucksLabel,
  type PickupLocationRow,
} from "./cascade-rows";

/**
 * Flat column set for the Excel export: one row per container group (see
 * `flattenLocationsForExport`), with the location's own columns repeated on
 * each of its rows. Values come from the same readers the table renders, so
 * the sheet and the screen always say the same thing.
 */
export const getPickupLocationsExportColumns = (t: TFn): Column<PickupLocationRow>[] => [
  {
    id: "rowNo",
    header: t(Namespace.Table, "number") || "№",
    accessor: (r) => String(r.exportIndex ?? ""),
  },
  {
    id: "object",
    header: t(Namespace.Table, "object"),
    accessor: (r) => locationNameLabel(r.location, t),
  },
  {
    id: "code",
    header: t(Namespace.PickupLocation, "cascade.code"),
    accessor: (r) => locationCode(r.location),
  },
  {
    id: "type",
    header: t(Namespace.Table, "locationType"),
    accessor: (r) => locationTypeLabel(r.location),
  },
  {
    id: "priority",
    header: t(Namespace.Table, "priority"),
    accessor: (r) => locationPriorityLabel(r.location, t),
  },
  {
    id: "worktime",
    header: t(Namespace.Table, "worktime"),
    accessor: (r) => locationWorktime(r.location, t),
  },
  {
    id: "containerGroup",
    header: t(Namespace.PickupLocation, "groups.table.containerGroups"),
    accessor: containerGroupLabel,
  },
  {
    id: "routeGroup",
    header: t(Namespace.PickupLocation, "table.routeGroup"),
    accessor: routeGroupsLabel,
  },
  {
    id: "responsibleTruck",
    header: t(Namespace.PickupLocation, "groups.responsibleTruck"),
    accessor: trucksLabel,
  },
  {
    id: "customer",
    header: t(Namespace.PickupLocation, "table.customer"),
    accessor: customerLabel,
  },
  {
    id: "contract",
    header: t(Namespace.PickupLocation, "table.contract"),
    accessor: (r) => contractLabel(r, t),
  },
  {
    id: "contact",
    header: t(Namespace.Table, "contact"),
    accessor: contactLabel,
  },
  {
    id: "contactNumber",
    header: t(Namespace.Table, "contactNumber"),
    accessor: (r) => contactPhone(r) || EMPTY_CELL,
  },
  {
    id: "pickupSchedule",
    header: t(Namespace.PickupLocation, "schedule.title"),
    accessor: (r) => pickupScheduleLabel(r, t),
  },
];
